// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartPageTemplate, { CartPageTemplateStyles } from "@guestsupply/App/Components/Cart/CartPageTemplate";
import CheckoutContentWrapper from "@guestsupply/App/Components/Checkout/CheckoutContentWrapper";
import DropShipOrdersModal from "@guestsupply/App/Components/Checkout/DropShipOrdersModal";
import XngZone from "@guestsupply/App/Components/Common/XngZone";
import setDropShipModalIsOpen from "@guestsupply/App/Handlers/AddToListModal/SetDropShipModalIsOpen";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { hasDropShipmentItemInCart } from "@guestsupply/App/Utilities/Cart.helper";
import { Stepper } from "@guestsupply/App/Widgets/Stepper/Stepper";
import { StepperContext } from "@guestsupply/App/Widgets/Stepper/StepperContext";
import { checkoutTitles, CHECKOUT_STEPS, XNGAGE_CUSTOM_WIDGET } from "@guestsupply/XDL/constants";
import { xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState) => ({
    cart: getCurrentCartState(state).value,
    enableDropShipModule: getSettingsCollection(state).customSettings.enableDropShipModule,
});

const mapDispatchToProps = {
    setDropShipModalIsOpen,
};

export interface CheckoutPageContainerStyles extends CartPageTemplateStyles {
    headerTitle?: TypographyPresentationProps;
    productListViewExpandLink?: LinkPresentationProps;
}

export const checkoutPageContainerStyles: CheckoutPageContainerStyles = {
    headerTitle: {
        variant: "h1",
        css: css`
            margin-bottom: 24px;
        `,
    },
    headerSection: {
        css: css`
            width: 100%;
            & > ${TypographyStyle} {
                &:not(:last-child) {
                    margin-bottom: 20px;
                    ${xScreen("lte", "md")} {
                        margin-bottom: 24px;
                    }
                    ${xScreen("lte", "sm")} {
                        margin-bottom: 16px;
                    }
                }
            }
        `,
    },
    totalSection: {
        css: css`
            ${xScreen("lte", "sm")} {
                & > div {
                    max-width: none;
                }
            }
        `,
    },
};

const styles = checkoutPageContainerStyles;

const CheckoutPageContainer: FC<Props> = ({ id, cart, enableDropShipModule, setDropShipModalIsOpen }) => {
    const { currentStep } = useContext(StepperContext);
    const { isAuth } = useAppSelector(state => {
        return {
            isAuth: xIsAuthenticated(state),
        };
    });

    const titleKey = isAuth ? "auth" : "unAuth";
    const isReview = currentStep === CHECKOUT_STEPS.ReviewAndPay;

    useEffect(() => {
        const dropShipInfoMissing = !cart?.properties?.dropShipContactInfo;
        const cartHasDropShipItem = hasDropShipmentItemInCart(cart?.cartLines);

        if (enableDropShipModule && cartHasDropShipItem && dropShipInfoMissing) {
            setDropShipModalIsOpen({ modalIsOpen: true });
        }
    }, [cart, enableDropShipModule]);

    return (
        <CartPageTemplate isReview={isReview} extendedStyles={styles}>
            <CartPageTemplate.MainSection>
                <CartPageTemplate.HeaderSection>
                    <Typography {...styles.headerTitle}>{translate("Checkout")}</Typography>
                    <Stepper />
                    <XngZone contentId={id} zoneName="Content0" hint="Zone For Header (Cart Approval message)" />
                </CartPageTemplate.HeaderSection>
                <CheckoutContentWrapper contentTitle={checkoutTitles[currentStep][titleKey]} isReview={isReview}>
                    <XngZone
                        contentId={id}
                        zoneName="Content1"
                        hint="Zone For Step Content (Shipping container, Submit & Review container)"
                    />
                </CheckoutContentWrapper>

                <AddToListModal />
                <DropShipOrdersModal />
            </CartPageTemplate.MainSection>
            <CartPageTemplate.CartTotalSection>
                <XngZone contentId={id} zoneName="Content2" hint="Zone For Checkout Total" />
                <XngZone contentId={id} zoneName="Content3" hint="Zone For Stepper Actions" />
            </CartPageTemplate.CartTotalSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutPageContainer),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Page Container",
        fieldDefinitions: [],
    },
};

export default widgetModule;
